import { Leaf } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useRecoilValue } from 'recoil';

import { ModeIcon } from '@/components/mode-icon';
import { UNIT_CO2, USER_ACCESS_FEATURES } from '@/lib/constants';
import { SegmentLinkModel } from '@/lib/models/segment/types';
import { getLocationByIdState } from '@/state/locations';
import { canAccessFeature } from '@/utils/auth';
import { convertModeVerb, formatDistance, formatDuration } from '@/utils/format';
import { formatEmissionsString } from '@/utils/format/emissions';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';

interface DirectionsRoadProps {
  segment: SegmentLinkModel;
  isPickup?: boolean;
}

const DirectionsRoad = ({ segment, isPickup }: DirectionsRoadProps) => {
  const { data: session } = useSession();
  const canSeeEmissions = canAccessFeature(USER_ACCESS_FEATURES.EMISSIONS, session);

  const { routeType, durationSeconds, distanceKm, fromId, toId, emissions } = segment;
  const fromPort = useRecoilValue(getLocationByIdState(fromId));
  const toPort = useRecoilValue(getLocationByIdState(toId));
  const destinationCode = toPort ? getPreferredIdentifier(toPort.identifiers)?.value : '';

  const { base, modeBar, subText, primaryText, emissionsBadge } = styles({ isPickup });

  return (
    <div className={base()}>
      <div className={modeBar()}>
        <ModeIcon mode={routeType} size="sm" />
      </div>
      {isPickup ? (
        <div className="pb-4 pl-8">
          <p className={primaryText()}>Pick up cargo {fromPort?.name}</p>
          <p className={subText()}>
            {convertModeVerb(routeType)} {toPort?.name} {destinationCode && `(${destinationCode})`}
          </p>
        </div>
      ) : (
        <div className="pl-8 pt-4">
          <p className={subText()}>
            {convertModeVerb(routeType)} {toPort?.name} {destinationCode && `(${destinationCode})`}
          </p>
          <p className={primaryText()}>Dropoff cargo {toPort?.name}</p>
        </div>
      )}
      <div className="py-4">
        <p className={subText()}>{formatDuration(durationSeconds)}</p>
      </div>
      <div className="hidden py-4 xl:block">
        <p className={subText()}>{formatDistance(distanceKm)}</p>
      </div>
      <div className="hidden py-4 xl:block">
        {canSeeEmissions ? (
          <div className={emissionsBadge()}>
            <Leaf />
            {emissions?.average && formatEmissionsString(emissions.average, emissions.unit)}
          </div>
        ) : (
          <p className={subText({ className: 'blur-[3px]' })}>N/A {UNIT_CO2}</p>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative grid grid-cols-[1fr_0.5fr] items-start px-4 leading-snug md:grid-cols-[var(--route-card-grid-cols-md)] xl:grid-cols-[var(--route-card-grid-cols-xl)]',
    primaryText: 'm-0 text-sm text-text-primary',
    subText: 'm-0 text-sm text-text-secondary',
    modeBar:
      'absolute bottom-0 left-4 top-0 flex w-5 items-end rounded-b-[10px] bg-grey-300 [&_div_span]:!bg-grey-400 [&_svg]:!fill-white',
    emissionsBadge:
      'inline-flex items-center gap-1 rounded-full bg-grey-50 px-1.5 py-0.5 text-sm text-grey-700 [&_svg]:size-3 [&_svg]:fill-green-500',
  },
  variants: {
    isPickup: {
      true: {
        base: 'items-end',
        modeBar: 'items-start rounded-b-[0px] rounded-t-[10px]',
      },
    },
  },
});

export { DirectionsRoad };
